import React, { useEffect, useState, useCallback } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const SEVERITY = {
  info: "#06B6D4",
  success: "#10B981",
  warning: "#F59E0B",
  critical: "#EF4444",
};

function relTime(d) {
  if (!d) return "";
  const t = new Date(d).getTime();
  const s = (Date.now() - t) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const res = await api.get("/notifications", { params: { limit: 20 } });
      setItems(res.data.items || []);
      setUnread(res.data.unread || 0);
    } catch (_e) {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // poll every 30s
    return () => clearInterval(t);
  }, [load]);

  async function markAllRead() {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch (_e) {}
  }

  async function openItem(n) {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (_e) {}
    }
    setOpen(false);
    load();
    if (n.action_url) navigate(n.action_url);
    else if (n.event === "incoming_call" || n.event === "consultation_scheduled") {
      if (n.request_id) navigate(`/teleconsult/room/${n.request_id}`);
    }
    else if (n.request_id) navigate(`/onboarding/requests/${n.request_id}`);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-button"
          className="relative w-9 h-9 rounded-lg border border-[#E5E7EB] flex items-center justify-center hover:bg-[#F5F7FA]"
        >
          <Bell className="w-4 h-4 text-[#1F2937]" />
          {unread > 0 && (
            <span
              data-testid="notifications-unread-count"
              className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-semibold flex items-center justify-center"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <div className="font-semibold text-[#1F2937]">Notifications</div>
            <div className="text-xs text-[#6B7280]">{unread} unread</div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={markAllRead}
            disabled={unread === 0}
            data-testid="notifications-mark-all-read"
            className="border-[#E5E7EB] text-[#1F2937]"
          >
            <CheckCheck className="w-4 h-4 mr-1.5" /> Mark all read
          </Button>
        </div>
        <div className="max-h-[440px] overflow-y-auto">
          {items.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-[#6B7280]" data-testid="notifications-empty">
              You're all caught up.
            </div>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => openItem(n)}
              data-testid={`notification-item-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-[#E5E7EB] last:border-b-0 hover:bg-[#F5F7FA] flex items-start gap-3 ${
                !n.read ? "bg-[#EAF8FA]/30" : ""
              }`}
            >
              <span
                className="mt-1.5 w-2 h-2 rounded-full shrink-0"
                style={{ background: SEVERITY[n.severity] || SEVERITY.info }}
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[#1F2937] truncate">{n.title}</div>
                {n.body && <div className="text-xs text-[#6B7280] mt-0.5 line-clamp-2">{n.body}</div>}
                <div className="text-[11px] text-[#94A3B8] mt-1">{relTime(n.created_at)}</div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
